<template>
  <div class="bg-white dark:bg-gray-800 rounded-xl shadow dark:shadow-lg p-6">
    <!-- Toast de éxito -->
    <transition name="fade">
      <div v-if="toast.show" :class="['fixed top-6 right-6 z-50 px-4 py-2 rounded shadow-lg font-semibold', 'bg-green-500 text-white', 'dark:bg-green-600']">
        {{ toast.message }}
      </div>
    </transition>
    <div class="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
      <div class="text-xl font-bold dark:text-white">Product List</div>
      <div class="flex gap-2">
        <input type="text" placeholder="Search Product" class="border rounded px-3 py-1 text-sm dark:bg-gray-900 dark:text-white dark:border-gray-700" />
        <button class="bg-violet-500 text-white px-4 py-1 rounded" @click="openAddModal">+ Add Product</button>
      </div>
    </div>
    <div class="overflow-x-auto">
      <table class="min-w-full text-sm">
        <thead>
          <tr class="bg-gray-100 dark:bg-gray-700 dark:text-white">
            <th class="px-4 py-2 text-left">Product</th>
            <th class="px-4 py-2 text-left">Category</th>
            <th class="px-4 py-2 text-left">Stock</th>
            <th class="px-4 py-2 text-left">Price</th>
            <th class="px-4 py-2 text-left">Status</th>
            <th class="px-4 py-2 text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="product in products" :key="product.id" class="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
            <td class="px-4 py-2 flex items-center gap-2">
              <img :src="product.img" class="w-8 h-8 rounded" />
              <span class="dark:text-white">{{ product.name }}</span>
            </td>
            <td class="px-4 py-2 dark:text-white">{{ product.category }}</td>
            <td class="px-4 py-2">
              <span :class="product.stock > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">{{ product.stock }}</span>
            </td>
            <td class="px-4 py-2 dark:text-white">${{ product.price }}</td>
            <td class="px-4 py-2">
              <span :class="product.status === 'Publish' ? 'bg-green-100 dark:bg-green-700 text-green-700 dark:text-green-200' : 'bg-yellow-100 dark:bg-yellow-700 text-yellow-700 dark:text-yellow-200'" class="px-2 py-1 rounded text-xs font-semibold">{{ product.status }}</span>
            </td>
            <td class="px-4 py-2 flex gap-2">
              <button class="text-violet-500 hover:underline" @click="openEditModal(product)">Edit</button>
              <button class="text-red-500 hover:underline" @click="confirmDelete(product)">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal para agregar/editar producto -->
    <div v-if="showModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 w-full max-w-md relative">
        <button @click="closeModal" class="absolute top-2 right-2 text-gray-400 hover:text-red-500">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
        <h2 class="text-xl font-bold mb-4 dark:text-white">{{ isEdit ? 'Edit Product' : 'Add Product' }}</h2>
        <form @submit.prevent="isEdit ? updateProduct() : addProduct()">
          <div class="mb-3">
            <label class="block text-sm font-medium mb-1 dark:text-gray-200">Name</label>
            <input v-model="form.name" required class="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:text-white dark:border-gray-700" />
          </div>
          <div class="mb-3">
            <label class="block text-sm font-medium mb-1 dark:text-gray-200">Category</label>
            <input v-model="form.category" required class="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:text-white dark:border-gray-700" />
          </div>
          <div class="mb-3">
            <label class="block text-sm font-medium mb-1 dark:text-gray-200">Stock</label>
            <input v-model.number="form.stock" type="number" min="0" required class="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:text-white dark:border-gray-700" />
          </div>
          <div class="mb-3">
            <label class="block text-sm font-medium mb-1 dark:text-gray-200">Price</label>
            <input v-model.number="form.price" type="number" min="0" step="0.01" required class="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:text-white dark:border-gray-700" />
          </div>
          <div class="mb-3">
            <label class="block text-sm font-medium mb-1 dark:text-gray-200">Status</label>
            <select v-model="form.status" class="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:text-white dark:border-gray-700">
              <option value="Publish">Publish</option>
              <option value="Scheduled">Scheduled</option>
            </select>
          </div>
          <div class="mb-3">
            <label class="block text-sm font-medium mb-1 dark:text-gray-200">Image URL</label>
            <input v-model="form.img" class="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:text-white dark:border-gray-700" />
          </div>
          <div class="flex justify-end gap-2 mt-4">
            <button type="button" @click="closeModal" class="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200">Cancel</button>
            <button type="submit" class="px-4 py-2 rounded bg-violet-500 text-white">{{ isEdit ? 'Update' : 'Add' }}</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal de confirmación para eliminar -->
    <div v-if="showDeleteModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 w-full max-w-sm relative">
        <button @click="showDeleteModal = false" class="absolute top-2 right-2 text-gray-400 hover:text-red-500">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
        <h2 class="text-xl font-bold mb-4 dark:text-white">Delete Product</h2>
        <p class="mb-4 dark:text-gray-200">Are you sure you want to delete <span class="font-semibold">{{ productToDelete?.name }}</span>?</p>
        <div class="flex justify-end gap-2">
          <button @click="showDeleteModal = false" class="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200">Cancel</button>
          <button @click="deleteProduct" class="px-4 py-2 rounded bg-red-500 text-white">Delete</button>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref } from 'vue';
const showModal = ref(false);
const isEdit = ref(false);
const editIndex = ref(null);
const showDeleteModal = ref(false);
const productToDelete = ref(null);
const toast = ref({ show: false, message: '' });
const products = ref([
  { id: 1, name: 'Zamit', img: 'https://randomuser.me/api/portraits/men/1.jpg', category: 'Accessories', stock: 332, price: 273.67, status: 'Publish' },
  { id: 2, name: 'Span', img: 'https://randomuser.me/api/portraits/men/2.jpg', category: 'Home Decor', stock: 898, price: 583.13, status: 'Scheduled' },
  { id: 3, name: 'Tampflex', img: 'https://randomuser.me/api/portraits/men/3.jpg', category: 'Accessories', stock: 908, price: 646.21, status: 'Scheduled' },
  { id: 4, name: 'Roldlamis', img: 'https://randomuser.me/api/portraits/men/4.jpg', category: 'Home Decor', stock: 361, price: 952.14, status: 'Publish' },
]);
const form = ref({ name: '', category: '', stock: 0, price: 0, status: 'Publish', img: '' });

function showToast(message) {
  toast.value = { show: true, message };
  setTimeout(() => {
    toast.value.show = false;
  }, 2000);
}

function openAddModal() {
  isEdit.value = false;
  form.value = { name: '', category: '', stock: 0, price: 0, status: 'Publish', img: '' };
  showModal.value = true;
}
function openEditModal(product) {
  isEdit.value = true;
  editIndex.value = products.value.findIndex(p => p.id === product.id);
  form.value = { ...product };
  showModal.value = true;
}
function closeModal() {
  showModal.value = false;
}
function addProduct() {
  products.value.push({
    id: Date.now(),
    ...form.value,
  });
  showToast('Product added successfully');
  closeModal();
  form.value = { name: '', category: '', stock: 0, price: 0, status: 'Publish', img: '' };
}
function updateProduct() {
  if (editIndex.value !== null && editIndex.value >= 0) {
    products.value[editIndex.value] = { ...form.value };
  }
  showToast('Product updated successfully');
  closeModal();
  form.value = { name: '', category: '', stock: 0, price: 0, status: 'Publish', img: '' };
  editIndex.value = null;
}
function confirmDelete(product) {
  productToDelete.value = product;
  showDeleteModal.value = true;
}
function deleteProduct() {
  products.value = products.value.filter(p => p.id !== productToDelete.value.id);
  showDeleteModal.value = false;
  showToast('Product deleted successfully');
  productToDelete.value = null;
}
</script>
<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style> 